import { ArtifactLightbox } from "@/components/artifacts/ArtifactLightbox.tsx";
import { AskUserForm } from "@/components/artifacts/AskUserForm.tsx";
import { useLightboxUrlState } from "@/components/artifacts/useLightboxUrlState.ts";
import { useSetBrowsedArtifact } from "@/components/artifacts/browsedArtifact.ts";
import { useCanvasActionContext } from "@/components/canvas/canvasContext.tsx";
import type { UserResponseArtifact } from "@/types/artifacts.ts";
import { toUrnString } from "@/types/urn.ts";
import { ListChecks } from "lucide-react";

/** Header icon and label a form carries wherever it is presented expanded. */
export const FORM_ARTIFACT_ICON = ListChecks;
export const FORM_ARTIFACT_TITLE = "Questions";

/**
 * The whole form, as every expanded surface renders it — the canvas card, the
 * omnibox biscuit's lightbox, and the in-thread chip's lightbox. One body so the
 * three can't drift on padding, disabled rules, or what "submitted" does.
 *
 * Every surface presents the form the same way, live or not: an expanded form
 * outlives the turn that raised it — the canvas stack is persisted per thread —
 * and a question the conversation scrolled past is still unanswered. Whether it
 * accepts input is `AskUserForm`'s call, made on the answers it already holds.
 */
export const FormArtifactBody = ({
  artifact,
  threadId,
  onSubmitted,
}: {
  artifact: UserResponseArtifact;
  threadId: number;
  onSubmitted?: () => void;
}) => {
  if (!artifact.form) return null;
  return (
    <div className="px-5 py-4">
      <AskUserForm
        formSchema={artifact.form}
        artifactId={artifact.id}
        threadId={threadId}
        messageId={artifact.messageId}
        onSubmitted={onSubmitted}
      />
    </div>
  );
};

/** The form in a dialog — the fallback wherever there is no canvas to present it
 *  on (mobile / gate off), and the only expanded surface on those clients. */
export const FormArtifactLightbox = ({
  artifact,
  threadId,
  open,
  onOpenChange,
}: {
  artifact: UserResponseArtifact;
  threadId: number;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) => (
  <ArtifactLightbox
    open={open}
    onOpenChange={onOpenChange}
    icon={FORM_ARTIFACT_ICON}
    title={FORM_ARTIFACT_TITLE}
  >
    <FormArtifactBody
      artifact={artifact}
      threadId={threadId}
      onSubmitted={() => onOpenChange(false)}
    />
  </ArtifactLightbox>
);

/**
 * Open a form the way every artifact opens: on the canvas, falling back to the
 * lightbox where there isn't one (mobile / gate off) or where the canvas
 * declines the URN.
 *
 * `surface` keys the lightbox's URL state, so the omnibox biscuit and the
 * in-thread chip for the *same* form don't both open off one param.
 */
export const useOpenFormArtifact = (
  artifact: UserResponseArtifact,
  threadId: number,
  surface: "biscuit" | "inline",
  /** The canvas took the form: a host that covers the canvas gets out of its way. */
  onOpenedOnCanvas?: () => void,
) => {
  const canvas = useCanvasActionContext();
  const setBrowsedArtifact = useSetBrowsedArtifact();
  const [isLightboxOpen, setLightboxOpen] = useLightboxUrlState(surface, artifact.id);
  const urn = toUrnString({ type: artifact.artifactType, id: artifact.id });

  // Point the omnibox card at the form too, so it describes what the canvas is
  // presenting. See {@link useSetBrowsedArtifact}.
  const openForm = () => {
    if (canvas?.resetTo(urn)) {
      setBrowsedArtifact({ threadId, artifact });
      onOpenedOnCanvas?.();
      return;
    }
    setLightboxOpen(true);
  };

  return { canvas, urn, isLightboxOpen, setLightboxOpen, openForm };
};
